// src/constants/ageRange.js

/**
 * Age Range Ordering System for Te Niwha - New Zealand National Antimicrobial Guidelines
 * 
 * This system automatically sorts pediatric age ranges in chronological order for questions
 * ending with "age of the patient?". All age ranges from the project CSV are included with proper ordering.
 * 
 * USAGE:
 * ------
 * // In StatelyForm.tsx
 * import { smartSortOptions } from '@constants/ageRangeConstants';
 * const sortedOptions = smartSortOptions(question, options);
 * 
 * DETECTION:
 * ----------
 * - Questions ending with "age of the patient?" → Age progression sorting
 * - All other questions → Alphabetical sorting
 * 
 * CHRONOLOGICAL ORDER EXAMPLES:
 * -----------------------------
 * Early: under 7 days → 7 to 20 days → 21 to 28 days → under 1 month
 * Infant: 1 to 2 months → 6 weeks to 3 months → 3 months to 2 years
 * Child: 1 to 3 years → 3 to 7 years → over 5 years → over 12 years
 * 
 * PREFIX HANDLING:
 * ---------------
 * Automatically extracts age ranges from prefixed text:
 * "Neonate 21 to 28 days" → "21 to 28 days" → sorts correctly
 * "Child; 3 to 7 years" → "3 to 7 years" → sorts correctly
 * "Infant - 1 to 3 months" → "1 to 3 months" → sorts correctly
 */

export const AGE_RANGE_ORDER = {
    
  // Very early (birth to 1 week)
  "Neonate in a NICU or SCBU": 0.01,
  "under 7 days": 0.1,
  "under 7 days (community)": 0.15,
  
  // Early neonatal (1-4 weeks)
  "7 to 20 days": 1.0,
  "7 to 28 days": 1.1,
  "7 to 28 days (community)": 1.15,
  "21 to 28 days": 1.2,
  "to 28 days": 1.3,
  "under 28 days": 1.4,
  
  // Late neonatal to early infant (1-3 months)
  "to 6 weeks": 2.0,
  "under 6 weeks": 2.1,
  "6 weeks to 3 months": 2.2,
  "6 weeks to 6 months": 2.3,
  "under 1 month": 2.4,
  "over 1 month": 2.5,
  "1 to 2 months": 2.6,
  "1 to 3 months": 2.7,
  "under 2 months": 2.8,
  "over 2 months": 2.9,
  "2 to 3 months": 3.0,
  "under 3 months": 3.1,
  "over 3 months": 3.2,
  
  // Early infant (3-12 months)
  "3 months to 2 years": 4.0,
  "3 months to 3 years": 4.1,
  "3 months to 12 years": 4.2,
  "under 6 months": 4.3,
  "over 6 months": 4.4,
  "under 1 year": 4.5,
  "1 month to 1 year": 4.6,
  
  // Toddler (1-3 years)
  "1 month to 2 years": 5.0,
  "1 to 3 years": 5.1,
  "over 2 years": 5.2,
  "2 months to 5 years": 5.3,
  "over 3 years": 5.4,
  
  // Child (3-12 years)
  "3 to 7 years": 6.0,
  "over 5 years": 6.1,
  "7 to 10 years": 6.2,
  "2 months to 12 years": 6.3,
  "2 to 12 years": 6.4,
  "1 month to 11 years": 6.5,
  "over 10 years": 6.6,
  "over 12 years": 6.7,
};

/**
 * Detects if a question is asking about age ranges
 * Simple rule: age questions always end with "age of the patient?"
 * @param {string} question - The question text
 * @returns {boolean}
 */
export const isAgeRangeQuestion = (question) => {
  return question.toLowerCase().endsWith('age of the patient?');
};

/**
 * Extract age range from prefixed text
 * Handles various prefixes like "Neonate", "Infant", "Child", "Child;", "Child -", etc.
 * @param {string} text - Text that may contain prefixed age range
 * @returns {string|null} Extracted age range or null if not found
 */
const extractAgeRangeFromText = (text) => {
  // Common prefixes that might appear before age ranges
  const prefixes = [
    'neonate', 'neonate or child', 'infant', 'child', 'toddler', 'adolescent', 'adult',
    'baby', 'newborn', 'pediatric', 'paediatric'
  ];
  
  const textLower = text.toLowerCase().trim();
  
  // Try to find age range patterns after removing prefixes and punctuation
  for (const prefix of prefixes) {
    // Look for prefix followed by optional punctuation/whitespace, then age range
    const prefixPattern = new RegExp(`^${prefix}[\\s;:,-]*(.+)$`, 'i');
    const match = textLower.match(prefixPattern);
    
    if (match) {
      const potentialAgeRange = match[1].trim();
      
      // Check if the extracted part looks like an age range
      const ageRangePatterns = [
        /\d+\s*to\s*\d+\s*(days?|weeks?|months?|years?)/,
        /under\s+\d+\s*(days?|weeks?|months?|years?)/,
        /over\s+\d+\s*(days?|weeks?|months?|years?)/,
        /to\s+\d+\s*(days?|weeks?|months?|years?)/,
        /\d+\s*(days?|weeks?|months?|years?)/
      ];
      
      if (ageRangePatterns.some(pattern => pattern.test(potentialAgeRange))) {
        return potentialAgeRange;
      }
    }
  }
  
  return null;
};

/**
 * Sort options by age range order using constants and prefix extraction only
 * @param {Array} options - Array of option objects
 * @returns {Array} Sorted options
 */
export const sortAgeRangeOptions = (options) => {
  return [...options].sort((a, b) => {
    const valueA = a.value || a;
    const valueB = b.value || b;
    
    // Try exact match in constants first
    let orderA = AGE_RANGE_ORDER[valueA];
    let orderB = AGE_RANGE_ORDER[valueB];
    
    // If no exact match, try to extract age range from prefixed text
    if (orderA === undefined) {
      const extractedA = extractAgeRangeFromText(valueA);
      if (extractedA) {
        orderA = AGE_RANGE_ORDER[extractedA];
      }
    }
    
    if (orderB === undefined) {
      const extractedB = extractAgeRangeFromText(valueB);
      if (extractedB) {
        orderB = AGE_RANGE_ORDER[extractedB];
      }
    }
    
    // Both found in constants (either direct or extracted)
    if (orderA !== undefined && orderB !== undefined) {
      return orderA - orderB;
    }
    
    // If only one found in constants, prioritize it
    if (orderA !== undefined && orderB === undefined) {
      return -1; // A comes first
    }
    
    if (orderA === undefined && orderB !== undefined) {
      return 1; // B comes first
    }
    
    // Neither found in constants - fall back to alphabetical
    return valueA.localeCompare(valueB);
  });
};

/**
 * Smart option sorter - age questions sorted by age progression, others alphabetically
 * @param {string} question - The question text
 * @param {Array} options - Array of option objects
 * @returns {Array} Appropriately sorted options
 */
export const smartSortOptions = (question, options) => {
  if (isAgeRangeQuestion(question)) {
    return sortAgeRangeOptions(options);
  }
  
  // Default alphabetical sorting for all other questions
  return [...options].sort((a, b) => {
    const valueA = a.value || a;
    const valueB = b.value || b;
    return valueA.localeCompare(valueB);
  });
};

// Export functions for testing purposes
export { extractAgeRangeFromText };